// AssignmentMoveModal.jsx — 담당 마케터 바꾸기 / 배정 끝내기 창 (공용 창, 휴대폰은 아래 시트)
//   2026-10-06 UX 5단계: 글자를 쉬운 말로, 칸·버튼 40px. 저장하는 값(payloadFromForm)은 그대로.
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../../components/Modal';
import { todayKST } from '../../../utils/date';

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 12 },
  summary: { border: '1px solid rgba(37,99,235,0.22)', borderRadius: 12, padding: 12, background: 'rgba(37,99,235,0.07)' },
  summaryTitle: { fontSize: 14, fontWeight: 900, color: 'var(--text)', marginBottom: 4 },
  helper: { fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(190px,100%),1fr))', gap: 10, minWidth: 0 },
  section: { border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: 'var(--bg-subtle)', minWidth: 0, boxSizing: 'border-box' },
  sectionTop: { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginBottom: 9 },
  sectionTitle: { fontSize: 13, fontWeight: 950, color: 'var(--text)' },
  field: { display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 },
  label: { fontSize: 12, fontWeight: 800, color: 'var(--text-muted)' },
  input: { height: 40, width: '100%', minWidth: 0, maxWidth: '100%', border: '1px solid var(--border)', borderRadius: 9, padding: '0 10px', background: 'var(--bg-card)', color: 'var(--text)', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
  toggle: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 40, fontSize: 13, color: 'var(--text-sub)', fontWeight: 900 },
  modeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(150px,100%),1fr))', gap: 8 },
  mode: (active) => ({
    minHeight: 68,
    border: active ? '2px solid #2563eb' : '1px solid var(--border)',
    borderRadius: 11,
    padding: 10,
    textAlign: 'left',
    background: active ? 'rgba(37,99,235,0.09)' : 'var(--bg-card)',
    color: 'var(--text)',
    cursor: 'pointer',
  }),
  modeTitle: { fontSize: 12, fontWeight: 950, marginBottom: 4 },
  modeText: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.35 },
  err: { fontSize: 12, color: '#dc2626', background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, padding: 8 },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' },
  btn: { minHeight: 40, border: '1px solid var(--border)', borderRadius: 8, padding: '0 12px', background: 'var(--bg-card)', color: 'var(--text-sub)', fontSize: 12, fontWeight: 900, cursor: 'pointer' },
  primary: { minHeight: 40, border: 'none', borderRadius: 8, padding: '0 15px', background: '#2563eb', color: '#fff', fontSize: 12, fontWeight: 950, cursor: 'pointer' },
};

function moneyInput(value) {
  const raw = String(value ?? '').replace(/,/g, '');
  if (raw === '') return '';
  if (!/^\d+$/.test(raw)) return null;
  return Number(raw).toLocaleString('ko-KR');
}

function numericText(value) {
  const raw = String(value ?? '').replace(/,/g, '');
  return raw === '' || /^\d+(\.\d{0,2})?$/.test(raw) ? raw : null;
}

function latestRule(assignment) {
  return [...(assignment?.billing_rules || [])]
    .sort((a, b) => String(b.effective_from || '').localeCompare(String(a.effective_from || '')))[0] || null;
}

function ruleForDate(assignment, date) {
  const rules = [...(assignment?.billing_rules || [])].sort((a, b) => String(b.effective_from || '').localeCompare(String(a.effective_from || '')));
  return rules.find((rule) => String(rule.effective_from || '') <= date && (!rule.effective_to || String(rule.effective_to) >= date)) || latestRule(assignment);
}

function sourceOf(rule) {
  return rule?.incentive_source || (rule?.incentive_enabled === false ? 'none' : 'direct');
}

function formFromRule(assignment, date) {
  const rule = ruleForDate(assignment, date) || {};
  return {
    date,
    salary_enabled: rule.salary_enabled !== false,
    monthly_salary: Number(rule.monthly_salary || assignment?.monthly_salary || 0).toLocaleString('ko-KR'),
    incentive_source: sourceOf(rule),
    incentive_unit_price: Number(rule.incentive_unit_price || 250).toLocaleString('ko-KR'),
    incentive_base_included_count: Number(rule.incentive_base_included_count || 0).toLocaleString('ko-KR'),
    leave_deduction_enabled: rule.leave_deduction_enabled !== false,
    free_leave_days: String(Number(rule.free_leave_days ?? 1)),
    reorder_excluded: rule.reorder_excluded !== false,
    cancel_deduction_enabled: rule.cancel_deduction_enabled !== false,
    assignment_notes: assignment?.notes || '',
    billing_rule_notes: rule.notes || '',
  };
}

function payloadFromForm(mode, form) {
  if (mode === 'end') return { end_date: form.date };
  const source = form.incentive_source || 'direct';
  return {
    start_date: form.date,
    salary_enabled: !!form.salary_enabled,
    monthly_salary: Number(String(form.monthly_salary || '0').replace(/,/g, '')) || 0,
    incentive_enabled: source !== 'none',
    incentive_source: source,
    incentive_unit_price: Number(String(form.incentive_unit_price || '0').replace(/,/g, '')) || 0,
    incentive_base_included_count: Number(String(form.incentive_base_included_count || '0').replace(/,/g, '')) || 0,
    leave_deduction_enabled: !!form.leave_deduction_enabled,
    free_leave_days: Number(form.free_leave_days || 0),
    reorder_excluded: !!form.reorder_excluded,
    cancel_deduction_enabled: !!form.cancel_deduction_enabled,
    assignment_notes: form.assignment_notes || null,
    billing_rule_notes: form.billing_rule_notes || null,
  };
}

export function AssignmentMoveModal({ mode, assignment, targetMarketer, saving, error, onClose, onSave }) {
  const initialDate = todayKST();
  const [form, setForm] = useState(() => formFromRule(assignment, initialDate));
  const [manualRuleTouched, setManualRuleTouched] = useState(false);
  const isEnd = mode === 'end';

  useEffect(() => {
    if (isEnd || manualRuleTouched) return;
    setForm((prev) => ({ ...formFromRule(assignment, prev.date), assignment_notes: prev.assignment_notes }));
  }, [assignment, form.date, isEnd, manualRuleTouched]);

  const title = isEnd ? '배정 끝내기' : '담당 마케터 바꾸기';
  const summary = useMemo(() => {
    if (isEnd) return `${assignment?.consultant_name || '-'} 배정을 끝냅니다 (담당 ${assignment?.marketer_name || '-'})`;
    return `${assignment?.consultant_name || '-'} 담당: ${assignment?.marketer_name || '-'} → ${targetMarketer?.name || '-'}`;
  }, [assignment, isEnd, targetMarketer]);

  function setField(key, value, touched = true) {
    if (touched && key !== 'date' && key !== 'assignment_notes') setManualRuleTouched(true);
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const footer = (
    <div style={S.footer}>
      <button type="button" style={S.btn} onClick={onClose} disabled={saving}>취소</button>
      <button type="button" style={S.primary} onClick={() => onSave(payloadFromForm(mode, form))} disabled={saving}>
        {saving ? '저장 중' : isEnd ? '배정 끝내기' : '담당 바꾸기'}
      </button>
    </div>
  );

  return (
    <Modal open title={title} icon="👤" onClose={onClose} footer={footer} width="min(760px, calc(100vw - 48px))" mobileSheet mobileSheetDragClose>
      <div style={S.wrap}>
        <div style={S.summary}>
          <div style={S.summaryTitle}>{summary}</div>
          <div style={S.helper}>
            {isEnd
              ? '마지막 근무일까지만 1:1급여에 넣고, 다음 날부터는 계산에서 빠져요. 이미 확정된 달 안의 날짜로는 끝낼 수 없어요.'
              : '정한 날 전날까지는 지금 마케터 배정으로 남고, 그날부터 새 마케터와 아래 급여 조건이 적용돼요.'}
          </div>
        </div>

        <label style={S.field}>
          <span style={S.label}>{isEnd ? '마지막 근무일' : '새 마케터 시작일'}</span>
          <input type="date" style={S.input} value={form.date} onChange={(e) => setField('date', e.target.value, false)} />
        </label>

        {!isEnd && (
          <>
            <div style={S.grid}>
              <div style={S.section}>
                <div style={S.sectionTop}>
                  <div style={S.sectionTitle}>월급</div>
                  <label style={S.toggle}><input type="checkbox" checked={form.salary_enabled} onChange={(e) => setField('salary_enabled', e.target.checked)} /> 계산에 넣기</label>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  style={S.input}
                  value={form.monthly_salary}
                  disabled={!form.salary_enabled}
                  onChange={(e) => {
                    const next = moneyInput(e.target.value);
                    if (next !== null) setField('monthly_salary', next);
                  }}
                />
              </div>
              <div style={S.section}>
                <div style={S.sectionTop}>
                  <div style={S.sectionTitle}>연차 차감</div>
                  <label style={S.toggle}><input type="checkbox" checked={form.leave_deduction_enabled} onChange={(e) => setField('leave_deduction_enabled', e.target.checked)} /> 계산에 넣기</label>
                </div>
                <label style={S.field}>
                  <span style={S.label}>무료 연차 일수</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    style={S.input}
                    value={form.free_leave_days}
                    disabled={!form.leave_deduction_enabled}
                    onChange={(e) => {
                      const next = numericText(e.target.value);
                      if (next !== null) setField('free_leave_days', next);
                    }}
                  />
                </label>
              </div>
            </div>

            <div style={S.section}>
              <div style={S.sectionTitle}>인센티브</div>
              <div style={{ ...S.modeGrid, marginTop: 8 }}>
                <button type="button" style={S.mode(form.incentive_source === 'direct')} onClick={() => setField('incentive_source', 'direct')}>
                  <div style={S.modeTitle}>장수로 직접 계산</div>
                  <div style={S.modeText}>인정 장수 × 1장당 금액으로 계산해요.</div>
                </button>
                <button type="button" style={S.mode(form.incentive_source === 'incentive_total')} onClick={() => setField('incentive_source', 'incentive_total')}>
                  <div style={S.modeTitle}>인센티브 관리 금액</div>
                  <div style={S.modeText}>인센티브 관리의 월 금액을 가져와요.</div>
                </button>
                <button type="button" style={S.mode(form.incentive_source === 'none')} onClick={() => setField('incentive_source', 'none')}>
                  <div style={S.modeTitle}>계산 안 함</div>
                  <div style={S.modeText}>1:1급여에 인센티브를 넣지 않아요.</div>
                </button>
              </div>

              {form.incentive_source === 'direct' && (
                <>
                  <div style={{ ...S.grid, marginTop: 10 }}>
                    <label style={S.field}>
                      <span style={S.label}>기본 포함 장수</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        style={S.input}
                        value={form.incentive_base_included_count}
                        onChange={(e) => {
                          const next = numericText(e.target.value);
                          if (next !== null) setField('incentive_base_included_count', next === '' ? '' : Number(next).toLocaleString('ko-KR'));
                        }}
                      />
                    </label>
                    <label style={S.field}>
                      <span style={S.label}>1장당 인센티브</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        style={S.input}
                        value={form.incentive_unit_price}
                        onChange={(e) => {
                          const next = moneyInput(e.target.value);
                          if (next !== null) setField('incentive_unit_price', next);
                        }}
                      />
                    </label>
                  </div>
                  <div style={{ ...S.grid, marginTop: 10 }}>
                    <label style={S.toggle}><input type="checkbox" checked={form.reorder_excluded} onChange={(e) => setField('reorder_excluded', e.target.checked)} /> 재계약 제외</label>
                    <label style={S.toggle}><input type="checkbox" checked={form.cancel_deduction_enabled} onChange={(e) => setField('cancel_deduction_enabled', e.target.checked)} /> 취소 장수 차감</label>
                  </div>
                </>
              )}
            </div>

            <label style={S.field}>
              <span style={S.label}>배정 메모</span>
              <input type="text" style={S.input} value={form.assignment_notes} onChange={(e) => setField('assignment_notes', e.target.value, false)} />
            </label>
          </>
        )}

        {error && <div style={S.err}>{error}</div>}
      </div>
    </Modal>
  );
}
